import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, RefreshCw, DollarSign, Users, TrendingUp, Activity,
  CheckCircle, AlertTriangle, Star, Target, Gauge, Car, Calendar,
  ClipboardList, FileText, BarChart3, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ExecutiveControlRoom = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/bi/dashboard`);
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const KPI = ({ icon: Icon, label, value, sub, color = 'gray' }) => {
    const colorMap = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      red: 'bg-red-100 text-red-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      purple: 'bg-purple-100 text-purple-600',
      teal: 'bg-teal-100 text-teal-600',
      orange: 'bg-orange-100 text-orange-600',
      gray: 'bg-gray-100 text-gray-600',
    };
    const cls = colorMap[color] || colorMap.gray;
    return (
      <Card className="p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${cls.split(' ')[0]}`}>
            <Icon className={`h-5 w-5 ${cls.split(' ')[1]}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xl sm:text-2xl font-bold truncate">{value}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">{label}</p>
            {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
          </div>
        </div>
      </Card>
    );
  };

  const BarRow = ({ label, value, max, color = 'bg-blue-500' }) => {
    const pct = max > 0 ? Math.min(value / max * 100, 100) : 0;
    return (
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-600 w-32 sm:w-40 truncate">{label}</span>
        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-semibold w-10 text-right">{value}</span>
      </div>
    );
  };

  const exec = data?.executive || {};
  const rev = exec.revenue || {};
  const crm = data?.crm || {};
  const pipeline = data?.pipeline || {};
  const growth = data?.growth || {};
  const service = data?.service || {};
  const rb = data?.real_bookings || {};
  const assess = data?.assessments || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="executive-control-room">
      {/* Header */}
      <header className="bg-black text-white py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white flex-shrink-0" data-testid="control-room-back-btn">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2 truncate">
                <Activity className="h-5 w-5 text-red-500 flex-shrink-0" />Executive Control Room
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {data?.generated_at && <span className="text-[10px] text-gray-500 hidden sm:inline">{new Date(data.generated_at).toLocaleString()}</span>}
            <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loading} className="text-black h-8" data-testid="refresh-dashboard-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {!data ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center"><RefreshCw className="h-12 w-12 animate-spin text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Loading dashboard...</p></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-6">

          {/* ====== TOP KPIs — Real Booking Data ====== */}
          <div>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Zap className="h-4 w-4 text-red-500" />Live Overview
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KPI icon={Calendar} label="Today's Bookings" value={rb.today_bookings || 0} color="red" />
              <KPI icon={DollarSign} label="Total Revenue" value={`$${(rb.total_revenue || 0).toLocaleString()}`} color="green" />
              <KPI icon={ClipboardList} label="Total Bookings" value={rb.total || 0} color="blue" />
              <KPI icon={DollarSign} label="Avg Booking" value={`$${(rb.avg_booking_value || 0).toLocaleString()}`} color="teal" />
              <KPI icon={Users} label="CRM Clients" value={crm.total_clients || 0} color="purple" />
              <KPI icon={FileText} label="Assessments" value={assess.total || 0} color="orange" />
            </div>
          </div>

          {/* ====== BOOKINGS BY STATUS + SERVICE TYPE ====== */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5" data-testid="bookings-by-status">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Bookings by Trip Status</h3>
              <div className="space-y-2.5">
                {Object.entries(rb.by_trip_status || {}).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                  const colors = { quoted: 'bg-gray-400', confirmed: 'bg-blue-500', dispatched: 'bg-yellow-500', en_route: 'bg-orange-500', completed: 'bg-green-500', billed: 'bg-purple-500' };
                  return <BarRow key={status} label={status.replace(/_/g, ' ')} value={count} max={rb.total || 1} color={colors[status] || 'bg-gray-400'} />;
                })}
                {Object.keys(rb.by_trip_status || {}).length === 0 && <p className="text-sm text-gray-400 text-center py-4">No booking data yet</p>}
              </div>
            </Card>

            <Card className="p-5" data-testid="bookings-by-service">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" />Bookings by Service Type</h3>
              <div className="space-y-2.5">
                {Object.entries(rb.by_service_type || {}).sort((a, b) => b[1] - a[1]).map(([svc, count]) => (
                  <BarRow key={svc} label={svc.replace(/-/g, ' ')} value={count} max={rb.total || 1} color="bg-red-500" />
                ))}
                {Object.keys(rb.by_service_type || {}).length === 0 && <p className="text-sm text-gray-400 text-center py-4">No service data yet</p>}
              </div>
            </Card>
          </div>

          {/* ====== FLEET UTILIZATION ====== */}
          <Card className="p-5" data-testid="fleet-utilization">
            <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Car className="h-4 w-4 text-amber-600" />Fleet Utilization — Top Vehicles</h3>
            {(rb.fleet_utilization || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200 text-[10px] text-gray-500 uppercase">
                      <th className="text-left py-2 pr-4">Vehicle</th>
                      <th className="text-center py-2 px-3">Bookings</th>
                      <th className="text-right py-2 pl-4">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rb.fleet_utilization || []).map((v, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2.5 pr-4 text-sm font-medium text-black truncate max-w-[200px]">{v.vehicle?.replace(/-/g, ' ')}</td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="inline-flex items-center justify-center h-6 w-10 rounded bg-blue-100 text-blue-700 text-xs font-bold">{v.bookings}</span>
                        </td>
                        <td className="py-2.5 pl-4 text-right font-bold text-green-700">${v.revenue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-gray-400 text-center py-4">No fleet utilization data yet</p>}
          </Card>

          {/* ====== REVENUE TREND + ASSESSMENT FUNNEL ====== */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Revenue Trend */}
            <Card className="p-5" data-testid="revenue-trend">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-green-600" />Monthly Revenue Trend</h3>
              {(rb.revenue_trend || []).length > 0 ? (
                <div className="space-y-2">
                  {(rb.revenue_trend || []).map((m) => {
                    const maxRev = Math.max(...(rb.revenue_trend || []).map(r => r.revenue), 1);
                    return (
                      <div key={m.month} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-16 font-mono">{m.month}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(m.revenue / maxRev * 100, 8)}%` }}>
                            <span className="text-[9px] font-bold text-white whitespace-nowrap">${m.revenue.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-gray-400 text-center py-4">No revenue data yet</p>}
            </Card>

            {/* Assessment Funnel */}
            <Card className="p-5" data-testid="assessment-funnel">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Target className="h-4 w-4 text-purple-600" />Assessment → Membership Interest</h3>
              {assess.total > 0 ? (
                <div className="space-y-2.5">
                  <div className="text-center p-3 bg-purple-50 rounded-lg mb-3">
                    <p className="text-2xl font-bold text-purple-700">{assess.total}</p>
                    <p className="text-xs text-purple-500">Total Assessments Submitted</p>
                  </div>
                  {Object.entries(assess.by_interest || {}).sort((a, b) => b[1] - a[1]).map(([tier, count]) => (
                    <BarRow key={tier} label={tier.replace(/-/g, ' ')} value={count} max={assess.total} color="bg-purple-500" />
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400 text-center py-4">No assessments yet. Share the assessment link to start collecting leads.</p>}
            </Card>
          </div>

          {/* ====== CRM + PIPELINE ====== */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5" data-testid="crm-overview">
              <h3 className="font-bold text-sm mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" />CRM Overview</span>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/crm')} className="text-xs h-7" data-testid="view-all-clients-btn">View All Clients</Button>
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-700">{crm.total_clients || 0}</p>
                  <p className="text-[10px] text-blue-500">Total Clients</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-700">${(crm.total_ltv || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-green-500">Total LTV</p>
                </div>
              </div>
              <div className="space-y-2">
                {Object.entries(crm.segments || {}).sort((a, b) => b[1] - a[1]).map(([seg, count]) => (
                  <BarRow key={seg} label={seg} value={count} max={crm.total_clients || 1} color="bg-blue-500" />
                ))}
              </div>
              {(crm.top_clients || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Top Clients</p>
                  {(crm.top_clients || []).slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-medium truncate">{c.name}</span>
                        {c.vip_level !== 'Standard' && <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold">{c.vip_level}</span>}
                      </div>
                      <span className="text-xs font-bold text-green-700 shrink-0">${(c.total_spent || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5" data-testid="pipeline-overview">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Gauge className="h-4 w-4 text-purple-600" />Consulting Pipeline</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{pipeline.total_intakes || 0}</p>
                  <p className="text-[10px] text-gray-500">Total Intakes</p>
                </div>
                <div className="text-center p-3 bg-teal-50 rounded-lg">
                  <p className="text-xl font-bold text-teal-700">{pipeline.conversion_rate || 0}%</p>
                  <p className="text-[10px] text-teal-500">Conversion Rate</p>
                </div>
              </div>
              <div className="space-y-2">
                {Object.entries(pipeline.stages || {}).map(([stage, count]) => {
                  const stageColors = { discovery: 'bg-blue-500', audit: 'bg-yellow-500', strategy: 'bg-purple-500', complete: 'bg-green-500' };
                  return <BarRow key={stage} label={stage} value={count} max={pipeline.total_intakes || 1} color={stageColors[stage] || 'bg-gray-400'} />;
                })}
              </div>
            </Card>
          </div>

          {/* ====== GROWTH + SERVICE QUALITY ====== */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5" data-testid="growth-metrics">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-teal-600" />Growth Engine</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{growth.upgrade_candidates || 0}</p>
                  <p className="text-[10px] text-gray-500">Upgrade Candidates</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{growth.referral_candidates || 0}</p>
                  <p className="text-[10px] text-gray-500">Referral Candidates</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{growth.public_bookings_percent || 0}%</p>
                  <p className="text-[10px] text-gray-500">Public Bookings</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{growth.member_bookings_percent || 0}%</p>
                  <p className="text-[10px] text-gray-500">Member Bookings</p>
                </div>
              </div>
            </Card>

            <Card className="p-5" data-testid="service-quality">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Star className="h-4 w-4 text-yellow-600" />Service Quality</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{service.on_time_rate || service.dispatch_compliance_percent || 'N/A'}%</p>
                  <p className="text-[10px] text-gray-500">On-Time Rate</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{service.avg_rating || service.avg_feedback_rating || 'N/A'}</p>
                  <p className="text-[10px] text-gray-500">Avg Rating</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{service.total_completed || 0}</p>
                  <p className="text-[10px] text-gray-500">Completed Jobs</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold">{service.service_quality_score || 0}</p>
                  <p className="text-[10px] text-gray-500">Quality Score</p>
                </div>
              </div>
            </Card>
          </div>

          {/* ====== RISK BOOKINGS ====== */}
          {exec.risk && exec.risk.length > 0 && (
            <Card className="p-5" data-testid="risk-bookings">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" />Risk Bookings</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200 text-[10px] text-gray-500 uppercase">
                      <th className="text-left py-2">Booking</th>
                      <th className="text-left py-2">Vehicle</th>
                      <th className="text-right py-2">Revenue</th>
                      <th className="text-right py-2">Margin</th>
                      <th className="text-center py-2">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exec.risk.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2 font-mono text-xs">{r.booking_id || `#${i + 1}`}</td>
                        <td className="py-2 text-xs text-gray-600">{r.vehicle || '—'}</td>
                        <td className="py-2 text-right text-xs font-medium">${r.revenue || 0}</td>
                        <td className="py-2 text-right text-xs">{r.margin || 0}%</td>
                        <td className="py-2 text-center"><span className={`text-[9px] font-bold px-2 py-0.5 rounded ${r.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.risk_level}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutiveControlRoom;
